import { FunctionComponent, memo, useMemo } from "react";
import { IProps } from "./dynamic-explore-filters.types";
import { formatEnumOptions } from "@edgetier/client-components";
import { DynamicFilterField, DynamicFilterGroup } from "@edgetier/dynamic-filter";
import { useFormikContext } from "formik";
import {
    faAnalytics,
    faComments,
    faGlobeAmericas,
    faSearch,
    faSpellCheck,
    faUser,
    faUserCheck,
} from "@fortawesome/pro-solid-svg-icons";
import { faAngry, faSmile, faThumbsUp } from "@fortawesome/pro-solid-svg-icons";
import AdvancedSearchPhrase from "components-for/filters/advanced-search-phrase";
import SelectInteractionType from "components-for/filters/select-interaction-type";
import SelectLanguage from "components-for/filters/select-language";
import { useSelectTagFields } from "hooks-for/filters/use-select-tag-fields";
import { IExploreFilters } from "../explore-filters.types";
import { SelectBoolean } from "@edgetier/select";
import SelectAgent from "components-for/filters/select-agent";
import { EXPERIENCE_SCORE_METRIC_ENABLED, WRITING_ISSUES_FILTER_ENABLED } from "constants/feature-flags";
import SelectWritingIssue from "components-for/filters/select-writing-issue";
import SelectMetricDistributions from "components-for/filters/select-metric-distributions";
import { createInteractionMetricParameterName, getVisibleTagGroupFields } from "./dynamic-explore-filters.utilities";
import { InteractionMetric } from "@edgetier/watchtower-types";
import { useFilterAvailableMetrics } from "hooks-for/metrics/use-filter-available-metrics";
import { useInteractionsMessageQueryParameters } from "hooks-for/interaction-messages/use-interactions-messages-query-parameters";
import { FilterGroups, FilterGroupsOrder } from "constants/filters";
import "./dynamic-explore-filters.scss";

/**
 * Filter fields that can be added or removed by the user.
 * @param props.hideFields            Names of fields that should not be displayed.
 * @param props.mandatoryFilterValues An object of values that will always be used, regardless of URL values.
 * @param props.customFieldItems      An object of field items that will filter current field items
 * @param props.visibleFields         Optional fields that should be displayed by default.
 * @param props.tagGroups             The tag groups that will the user can use for filters.
 */
const DynamicExploreFilters: FunctionComponent<IProps> = ({
    hideFields = [],
    mandatoryFilterValues = {},
    customFieldItems = {},
    visibleFields = [],
    tagGroups,
}) => {
    const { values, setFieldValue } = useFormikContext<IExploreFilters>();

    // These components are returned from a hook and not just wrapped in a component because you can't nest children of
    // DynamicFilterGroup.
    const { tagFields } = useSelectTagFields(tagGroups);

    // Some tag groups are marked as common and they should always be visible.
    const visibleTagGroupFields = useMemo(() => getVisibleTagGroupFields(tagGroups ?? []), [tagGroups]);

    const interactionMetricExcludeOptions = [
        InteractionMetric.QueryResolution,
        ...(EXPERIENCE_SCORE_METRIC_ENABLED ? [] : [InteractionMetric.ExperienceScore]),
    ];
    const metricsAvailable = useFilterAvailableMetrics(interactionMetricExcludeOptions);
    const formattedInteractionMetrics = formatEnumOptions(metricsAvailable, interactionMetricExcludeOptions);

    const { agentId, interactionEndDateTime, interactionStartDateTime, languageIsoCode } =
        useInteractionsMessageQueryParameters({
            additionalParameters: values ?? {},
            prefixValue: "interaction",
            excludedParameters: ["agentId", "languageIsoCode"],
        });

    return (
        <DynamicFilterGroup
            hideFields={hideFields}
            initialVisibleFields={[...visibleFields, ...visibleTagGroupFields]}
            onRemoveFilter={(name, alternateNames = []) => {
                setFieldValue(name, null);
                alternateNames
                    .filter((alternateName) => alternateName !== name)
                    .forEach((alternateName) => setFieldValue(alternateName, null));
            }}
        >
            <DynamicFilterField
                filterCanBeRemoved={!("search" in mandatoryFilterValues)}
                icon={faSearch}
                label="Phrase"
                name="search"
                group={FilterGroups.General}
                groupOrder={FilterGroupsOrder[FilterGroups.General]}
            >
                <AdvancedSearchPhrase
                    isDisabled={"search" in mandatoryFilterValues}
                    name="search"
                    placeholder="Search&hellip;"
                />
            </DynamicFilterField>

            <DynamicFilterField
                filterCanBeRemoved={!("interactionType" in mandatoryFilterValues)}
                icon={faComments}
                label="Interaction Type"
                name="interactionType"
                group={FilterGroups.General}
                groupOrder={FilterGroupsOrder[FilterGroups.General]}
            >
                <SelectInteractionType
                    isDisabled={"interactionType" in mandatoryFilterValues}
                    isSingleSelect={"interactionType" in customFieldItems}
                    customFieldItems={customFieldItems.interactionType}
                />
            </DynamicFilterField>

            <DynamicFilterField
                filterCanBeRemoved={!("languageIsoCode" in mandatoryFilterValues)}
                icon={faGlobeAmericas}
                label="Language"
                name="languageIsoCode"
                group={FilterGroups.General}
                groupOrder={FilterGroupsOrder[FilterGroups.General]}
            >
                <SelectLanguage
                    isClearable={typeof values.search === "undefined" || values.search === ""}
                    isDisabled={"languageIsoCode" in mandatoryFilterValues}
                    isSingleSelect
                    parameters={{ interactionEndDateTime, interactionStartDateTime }}
                    mustInclude={languageIsoCode}
                    name="languageIsoCode"
                />
            </DynamicFilterField>

            {tagFields}

            <DynamicFilterField
                filterCanBeRemoved={!("agentId" in mandatoryFilterValues)}
                name="agentId"
                label="Agent"
                icon={faUser}
                group={FilterGroups.General}
                groupOrder={FilterGroupsOrder[FilterGroups.General]}
            >
                <SelectAgent
                    isDisabled={"agentId" in mandatoryFilterValues}
                    name="agentId"
                    mustInclude={agentId}
                    parameters={{ interactionEndDateTime, interactionStartDateTime }}
                />
            </DynamicFilterField>

            <DynamicFilterField
                filterCanBeRemoved={!("hasFrustration" in mandatoryFilterValues)}
                name="hasFrustration"
                label="Frustration"
                icon={faAngry}
                group={FilterGroups.Emotions}
                groupOrder={FilterGroupsOrder[FilterGroups.Emotions]}
            >
                <SelectBoolean
                    description="frustration"
                    falseLabel="No frustration"
                    name="hasFrustration"
                    trueLabel="Has frustration"
                />
            </DynamicFilterField>
            <DynamicFilterField
                filterCanBeRemoved={!("hasGratitude" in mandatoryFilterValues)}
                name="hasGratitude"
                label="Gratitude"
                icon={faSmile}
                group={FilterGroups.Emotions}
                groupOrder={FilterGroupsOrder[FilterGroups.Emotions]}
            >
                <SelectBoolean
                    description="gratitude"
                    falseLabel="No gratitude"
                    name="hasGratitude"
                    trueLabel="Has gratitude"
                />
            </DynamicFilterField>
            <DynamicFilterField
                filterCanBeRemoved={!("hasPraise" in mandatoryFilterValues)}
                name="hasPraise"
                label="Praise"
                icon={faThumbsUp}
                group={FilterGroups.Emotions}
                groupOrder={FilterGroupsOrder[FilterGroups.Emotions]}
            >
                <SelectBoolean description="praise" falseLabel="No praise" name="hasPraise" trueLabel="Has praise" />
            </DynamicFilterField>

            {WRITING_ISSUES_FILTER_ENABLED && (
                <DynamicFilterField
                    name="writingIssueType"
                    label="Writing Issue"
                    icon={faSpellCheck}
                    group={FilterGroups.Metrics}
                    groupOrder={FilterGroupsOrder[FilterGroups.Metrics]}
                >
                    <SelectWritingIssue name="writingIssueType" />
                </DynamicFilterField>
            )}

            <DynamicFilterField
                name="isQueryResolved"
                label="Query Resolution"
                icon={faUserCheck}
                group={FilterGroups.Metrics}
                groupOrder={FilterGroupsOrder[FilterGroups.Metrics]}
            >
                <SelectBoolean
                    description="query resolution"
                    falseLabel="Not resolved"
                    name="isQueryResolved"
                    trueLabel="Resolved"
                />
            </DynamicFilterField>

            {formattedInteractionMetrics.map((metricType) => {
                const minimumName = createInteractionMetricParameterName(metricType.value, "minimum");
                const maximumName = createInteractionMetricParameterName(metricType.value, "maximum");
                return (
                    <DynamicFilterField
                        key={metricType.value}
                        name={maximumName}
                        names={[maximumName, minimumName]}
                        label={metricType.label}
                        icon={faAnalytics}
                        group={FilterGroups.Metrics}
                        groupOrder={FilterGroupsOrder[FilterGroups.Metrics]}
                    >
                        <SelectMetricDistributions
                            label={metricType.label}
                            metricType={metricType.value}
                            maximumFieldName={maximumName}
                            minimumFieldName={minimumName}
                        />
                    </DynamicFilterField>
                );
            })}
        </DynamicFilterGroup>
    );
};

export default memo(DynamicExploreFilters);
